package ru.chm.gradereport.ui.screens.home

import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import ru.chm.gradereport.data.GradeApi
import ru.chm.gradereport.model.CourseDto
import ru.chm.gradereport.model.UserDto
import ru.chm.gradereport.model.UserRole
import ru.chm.gradereport.ui.components.AppCard
import ru.chm.gradereport.ui.components.ConfirmDialog
import ru.chm.gradereport.ui.components.ErrorBanner
import ru.chm.gradereport.ui.components.HeaderAction
import ru.chm.gradereport.ui.components.LoadingBox
import ru.chm.gradereport.ui.components.MutedText
import ru.chm.gradereport.ui.components.ScreenHeader
import ru.chm.gradereport.ui.components.ScreenScaffold
import ru.chm.gradereport.ui.components.SectionTitle
import ru.chm.gradereport.ui.theme.GradeTheme

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun HomeScreen(
    api: GradeApi,
    user: UserDto,
    onOpenCourse: (CourseDto) -> Unit,
    onOpenReviewers: () -> Unit,
    onLogout: () -> Unit,
) {
    var courses by remember { mutableStateOf<List<CourseDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var name by remember { mutableStateOf("") }
    var description by remember { mutableStateOf("") }
    var pendingDeleteCourse by remember { mutableStateOf<CourseDto?>(null) }
    var pendingEditCourse by remember { mutableStateOf<CourseDto?>(null) }
    val scope = rememberCoroutineScope()
    val isAdmin = user.role == UserRole.ADMIN

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                courses = api.listCourses()
            } catch (e: Exception) {
                error = e.message
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(Unit) { reload() }

    ScreenScaffold {
        ScreenHeader(
            title = "ЦХМ Учебная ведомость",
            subtitle = "${user.displayName} · ${if (isAdmin) "Администратор" else "Проверяющий"} · UID ${user.uid}",
            actions = buildList {
                if (isAdmin) {
                    add(HeaderAction("Проверяющие", onClick = onOpenReviewers))
                }
                add(HeaderAction("Выйти", onClick = onLogout))
            },
        )

        ErrorBanner(error, onDismiss = { error = null })
        SectionTitle(
            if (isAdmin) "Курсы" else "Мои курсы",
            modifier = Modifier.padding(top = 8.dp, bottom = 12.dp),
        )

        if (isAdmin) {
            CreateCourseCard(
                name = name,
                onNameChange = { name = it },
                description = description,
                onDescriptionChange = { description = it },
                onCreate = {
                    scope.launch {
                        try {
                            api.createCourse(name.trim(), description.trim())
                            name = ""
                            description = ""
                            reload()
                        } catch (e: Exception) {
                            error = e.message
                        }
                    }
                },
            )
        }

        if (loading) {
            LoadingBox()
        } else if (courses.isEmpty()) {
            AppCard(modifier = Modifier.fillMaxWidth()) {
                MutedText(
                    if (isAdmin) "Пока нет курсов — создайте первый."
                    else "Вам ещё не назначены курсы. Обратитесь к администратору.",
                )
            }
        } else {
            BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
                val narrow = maxWidth < 640.dp
                FlowRow(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp),
                ) {
                    courses.forEach { course ->
                        CourseListCard(
                            course = course,
                            isAdmin = isAdmin,
                            onOpen = { onOpenCourse(course) },
                            onEdit = { pendingEditCourse = course },
                            onDelete = { pendingDeleteCourse = course },
                            modifier = if (narrow) {
                                Modifier.fillMaxWidth()
                            } else {
                                Modifier
                                    .fillMaxWidth(0.48f)
                                    .widthIn(min = 280.dp)
                            },
                        )
                    }
                }
            }
        }
    }

    pendingDeleteCourse?.let { course ->
        ConfirmDialog(
            title = "Удалить курс?",
            message = "Курс «${course.name}» и все связанные данные (студенты, занятия, оценки) будут удалены безвозвратно.",
            onConfirm = {
                scope.launch {
                    try {
                        api.deleteCourse(course.id)
                        pendingDeleteCourse = null
                        reload()
                    } catch (e: Exception) {
                        pendingDeleteCourse = null
                        error = e.message
                    }
                }
            },
            onDismiss = { pendingDeleteCourse = null },
        )
    }

    pendingEditCourse?.let { course ->
        EditCourseDialog(
            course = course,
            onDismiss = { pendingEditCourse = null },
            onConfirm = { newName, newDescription ->
                scope.launch {
                    try {
                        api.updateCourse(course.id, newName, newDescription)
                        pendingEditCourse = null
                        reload()
                    } catch (e: Exception) {
                        pendingEditCourse = null
                        error = e.message
                    }
                }
            },
        )
    }
}
